import { useDialog } from '@context/dialog';
import { HostedCampaign } from '@givz/core-client';
import { useCoreClientMutation } from '@queries/client';
import { useData } from '@hooks/form';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { FunctionComponent } from 'react';

export type UseDuplicateIncentiveDialog = {
  onDone: (data: HostedCampaign) => void;
};

export const useDuplicateIncentiveDialog = (
  props: UseDuplicateIncentiveDialog,
) => {
  const { onDone } = props;
  const { closeDialog, prepareDialog, showDialog } = useDialog();
  const { mutate: duplicateIncentive } = useCoreClientMutation({
    mutationFn: (client) => client.Hosted.campaigns.duplicate,
    onSuccess: onDone,
  });

  const showDialogAction = (params: { gid: string; name: string }) => {
    const { gid, name } = params;

    const onDoneDialog: DuplicateIncentiveDialog['onDone'] = (data) => {
      closeDialog();
      const { amount, name } = data;
      duplicateIncentive({ gid, hostedCampaignDuplicateDto: { amount, name } });
    };

    prepareDialog({
      content: (
        <DuplicateIncentiveDialog
          onDone={onDoneDialog}
          name={name}
          onCancel={closeDialog}
        />
      ),
      dialogProps: { fullWidth: true, maxWidth: 'sm' },
    });

    showDialog();
  };

  return {
    closeDialog,
    showDialog: showDialogAction,
  };
};

export type DuplicateIncentiveDialog = {
  name: string;
  onDone: (props: { amount: number; name: string }) => void;
  onCancel: () => void;
};

export type DuplicateIncentiveDialogState = {
  name: string;
  amount?: number;
};

export const DuplicateIncentiveDialog: FunctionComponent<
  DuplicateIncentiveDialog
> = (props) => {
  const { name, onCancel, onDone } = props;
  const { data, setValueFromKey } = useData<DuplicateIncentiveDialogState>({
    name,
  });

  const onDoneAction = () => {
    const { amount, name } = data;

    if (name && amount) {
      onDone({ amount, name });
    }
  };

  return (
    <>
      <DialogTitle>Duplicate Incentive</DialogTitle>
      <DialogContent dividers>
        <Stack direction="column" spacing={1}>
          <TextField
            id="input-with-icon-textfield"
            label="Name"
            variant="outlined"
            value={data.name}
            onChange={(event) => setValueFromKey('name')(event.target.value)}
            required
            error={!data.name}
          />
          <TextField
            id="input-with-icon-textfield"
            label="Amount"
            variant="outlined"
            value={data.amount}
            onChange={(event) =>
              setValueFromKey('amount')(Number(event.target.value))
            }
            type="number"
            required
            error={!data.amount}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onCancel} variant="text">
          Cancel
        </Button>
        <Button onClick={onDoneAction} variant="outlined">
          Done
        </Button>
      </DialogActions>
    </>
  );
};
